import { Button, Modal, Text, TextInput } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import * as InvoiceApi from "../../apis/invoice";
import { handleError } from "../../utils/error-handler";
import { message } from "../../utils/Message";
import { AppModalHeader, ModalFooter } from "../ui/app-modal";

type Props = {
  invoiceId: string;
  opened: boolean;
  onClose: () => void;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Send a copy of the invoice PDF by e-mail, in any invoice status. */
export const DeliverCopyModal = ({ invoiceId, opened, onClose }: Props) => {
  const { t } = useTranslation(["invoice", "common"]);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const trimmed = email.trim();
  const invalid = trimmed.length > 0 && !EMAIL_RE.test(trimmed);

  const submit = async () => {
    setSending(true);
    try {
      const res: any = await InvoiceApi.deliverCopy(invoiceId, trimmed ? { email: trimmed } : {});
      message.success(t("invoice:detail.actions.deliverCopySent", { email: res?.sentTo ?? trimmed }));
      setEmail("");
      onClose();
    } catch (error) {
      handleError(error);
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} withCloseButton={false} padding={0} centered size="md">
      <AppModalHeader title={t("invoice:detail.actions.deliverCopyTitle")} onClose={onClose} closeDisabled={sending} />
      <div style={{ padding: "16px 24px" }}>
        <TextInput
          label={t("invoice:detail.actions.deliverCopyEmail")}
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          error={invalid}
          type="email"
        />
        <Text size="sm" c="dimmed" mt={6}>
          {t("invoice:detail.actions.deliverCopyEmailHint")}
        </Text>
      </div>
      <ModalFooter>
        <Button variant="default" onClick={onClose} disabled={sending}>
          {t("common:cancel")}
        </Button>
        <Button color="brand" onClick={submit} loading={sending} disabled={invalid}>
          {t("invoice:detail.actions.deliverCopySubmit")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
